import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box } from "@mui/material";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";

import { GridColDef } from "@mui/x-data-grid-premium";
import GaneshDataGrid from "../../../components/DataGrid/GaneshDataGrid";
import { renderNumberCell } from "utils/dataGrid";

interface BrandMetadataReviewProps {
  brand_id: string;
}

const columns_def: GridColDef[] = [
  {
    field: "chain_id",
    headerName: "Chain ID",
    type: "string",
    width: 100,
  },
  {
    field: "metadata_country",
    headerName: "Metadata Country",
    description:
      "Country code pulled from estimated counts value from this brand's metadata.",
    type: "string",
    width: 175,
  },
  {
    field: "estimated_count",
    headerName: "Estimated Count",
    description: "Estimated count for this country from this brand's metadata.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "actual_country",
    headerName: "Places Country",
    description: "Country code pulled from places table for this chain_id.",
    type: "string",
    width: 150,
  },
  {
    field: "actual_count",
    headerName: "Places Count",
    description:
      "Count of places associated with this brand's chain_id in this country.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
];

const ReviewBrandCountries: React.FC<BrandMetadataReviewProps> = ({
  brand_id,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  useEffect(() => {
    if (brand_id) {
      const fetchBrandCountries = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/estimated_counts_by_country/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand countries to review for ${brand_id}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandCountries();
    }
  }, []);

  const user_instructions = {
    section_id: "brand-countries",
    instruction_title: "Review List of Countries",
    purpose:
      "Having accurate information about the countries where the brand operates is essential for assessing our POI coverage, including determining `omniscraper` coverage. To evaluate coverage, the table below lists the countries associated with places in the dataplor database that are linked to the brand under review (i.e., places in the database with a `chain_id` matching the brand being analyzed).",
    goal: "Ensure all countries where the brand operates are accurately reflected in the metadata.",
    task_description:
      "The table below compares the countries listed in the `estimated_counts_by_country` attribute from the metadata with the countries associated with places in the dataplor database for the brand.",
    task_steps: [
      {
        title: "Identify Discrepancies",
        instructions: [
          "Review the table to identify any country codes present in the database but missing from the metadata.",
        ],
      },
      {
        title: "Validate Missing Country Codes",
        instructions: [
          "Research each missing country code on the web to verify whether the brand operates in that country.",
          "Use reliable sources such as the brand’s website, official announcements, or credible business directories.",
        ],
      },
      {
        title: "Update the Metadata",
        instructions: [
          {
            text: "For verified countries, update the `estimated_counts_by_country` attribute in the metadata in `Kuebiko`.",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Brand Edit`.",
              "Update the metadata to include the missing countries.",
            ],
          },
          "Ensure that the `estimated_counts` value reflects the brand’s presence in that country, either by estimating the count or leaving it as `000` if the exact number is unavailable.",
        ],
      },
    ],
    note: "The goal of this task is to verify the proper countries are listed in the metadata. You do not need to validate estimated counts at this stage.",
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      <GaneshDataGrid
        gridId="reviewBrandCountriesGrid"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={false}
        customHeight="750px"
      />
    </Box>
  );
};

export default ReviewBrandCountries;
